import React, { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ro from "date-fns/locale/ro";
import { ReactSVG } from "react-svg";

import arrowIcon from "../../assets/svg/arrow.svg";
import heartFullIcon from "../../assets/svg/mingcute--heart-fill.svg";
import {
   bookPracticalExamSlot,
   getAvailablePracticalExamSlots,
   getMyPracticalExamHistory,
   getMyPracticalExamReservation,
} from "../../api/practicalExamSlotsService";
import { useUserContext } from "../../UserContext";
import { usePracticalExamBookingWindow } from "../../hooks/useStudentBookingWindow";
import {
   isPracticalExamBookingWindowOpen,
   normalizeStudentBookingSector,
   STUDENT_BOOKING_SECTOR_OPTIONS,
   PRACTICAL_EXAM_BOOKING_WINDOW_BLOCKED_MESSAGE,
   PRACTICAL_EXAM_BOOKING_WINDOW_FULL_LABEL,
} from "../../utils/studentBookingWindow";
import {
   normalizePracticalExamHour,
   PRACTICAL_EXAM_RESERVATION_DEFAULT_COLOR,
   PRACTICAL_EXAM_TIME_GRID_COLUMNS,
   PRACTICAL_EXAM_TIME_OPTIONS,
} from "../../constants/practicalExamCalendar";

registerLocale("ro", ro);

const GEARBOX_OPTIONS = [
   { value: "manual", label: "Manual" },
   { value: "automat", label: "Automat" },
];
const MAX_SELECTABLE_MONTHS = 3;

const FINAL_STATUSES = new Set([
   "PASSED",
   "PROMOTED",
   "PROMOVAT",
   "ADMIS",
   "FAILED",
   "RESPINS",
   "ABSENT",
   "CANCELLED",
   "CANCELED",
   "ANULAT",
]);

const STATUS_LABELS = {
   SCHEDULED: "Programat",
   PASSED: "Promovat",
   PROMOTED: "Promovat",
   PROMOVAT: "Promovat",
   ADMIS: "Promovat",
   FAILED: "Respins",
   RESPINS: "Respins",
   ABSENT: "Absent",
   CANCELLED: "Anulat",
   CANCELED: "Anulat",
   ANULAT: "Anulat",
};

function readFirst(source, keys, fallback = null) {
   if (!source || typeof source !== "object") return fallback;

   for (const key of keys) {
      const value = source?.[key];
      if (value !== undefined && value !== null && value !== "") return value;
   }

   return fallback;
}

function unwrapOne(raw) {
   if (!raw || typeof raw !== "object") return null;
   if (raw.data === null || raw.reservation === null || raw.item === null) {
      return null;
   }
   if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
      return raw.data;
   }
   if (
      raw.reservation &&
      typeof raw.reservation === "object" &&
      !Array.isArray(raw.reservation)
   ) {
      return raw.reservation;
   }
   if (raw.item && typeof raw.item === "object" && !Array.isArray(raw.item)) {
      return raw.item;
   }
   if (raw.id != null || raw._id != null || raw.slotId != null) return raw;
   return null;
}

function normalizeStatus(status) {
   return String(status || "SCHEDULED")
      .trim()
      .toUpperCase();
}

function readReservationStatus(reservation) {
   return normalizeStatus(
      readFirst(
         reservation,
         ["status", "result", "examStatus", "exam_status", "examResult", "verdict"],
         "SCHEDULED",
      ),
   );
}

function isPassedStatus(status) {
   const normalized = normalizeStatus(status);
   return (
      normalized === "PASSED" ||
      normalized === "PROMOTED" ||
      normalized === "PROMOVAT" ||
      normalized === "ADMIS" ||
      normalized.includes("PASS")
   );
}

function isPassedReservation(reservation) {
   return isPassedStatus(readReservationStatus(reservation));
}

function isIgnorableHistoryError(error) {
   const message = String(error?.message || "");
   return (
      message.includes("getMyPracticalExamHistory 400") ||
      message.includes("numeric string is expected")
   );
}

function isNoActiveReservationError(error) {
   const message = String(error?.message || "").toLowerCase();
   return (
      message.includes("getmypracticalexamreservation 404") ||
      message.includes("not found") ||
      message.includes("nu există") ||
      message.includes("nu exista") ||
      message.includes("negăsit") ||
      message.includes("negasit")
   );
}

function formatStatus(status) {
   const normalized = normalizeStatus(status);
   return STATUS_LABELS[normalized] || normalized || "—";
}

function hasPracticalExamFlag(user) {
   if (!user || typeof user !== "object") return false;
   return (
      Object.prototype.hasOwnProperty.call(user, "canBookPracticalExam") ||
      Object.prototype.hasOwnProperty.call(user, "can_book_practical_exam") ||
      Object.prototype.hasOwnProperty.call(user, "practicalExamAccess") ||
      Object.prototype.hasOwnProperty.call(user, "practical_exam_access")
   );
}

function readCanBookPracticalExam(user) {
   const value = readFirst(user, [
      "canBookPracticalExam",
      "can_book_practical_exam",
      "practicalExamAccess",
      "practical_exam_access",
   ]);

   if (typeof value === "boolean") return value;
   if (value === 1 || value === "1") return true;
   if (value === 0 || value === "0") return false;
   if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "true") return true;
      if (normalized === "false") return false;
   }

   return true;
}

function readSlotFromReservation(reservation) {
   return (
      reservation?.slot ??
      reservation?.practicalExamSlot ??
      reservation?.examSlot ??
      reservation?.PracticalExamSlot ??
      reservation?.practical_exam_slot ??
      null
   );
}

function readSlotId(slot) {
   return readFirst(slot, ["id", "_id", "slotId", "slot_id"]);
}

function readReservationSlot(reservation) {
   const nested = readSlotFromReservation(reservation);
   return nested || reservation || {};
}

function readDate(source) {
   const date = readFirst(source, [
      "dateKey",
      "date",
      "slotDate",
      "slot_date",
      "examDate",
      "exam_date",
      "startDate",
      "start_date",
      "day",
      "startAt",
      "start_at",
      "startsAt",
      "starts_at",
   ]);

   if (typeof date === "string" && date.includes("T")) {
      return date.slice(0, 10);
   }

   return date ? String(date).slice(0, 10) : "";
}

function readTime(source) {
   const time = readFirst(source, [
      "timeKey",
      "time",
      "slotTime",
      "slot_time",
      "examTime",
      "exam_time",
      "startTime",
      "start_time",
      "startHour",
      "start_hour",
      "startAt",
      "start_at",
      "startsAt",
      "starts_at",
   ]);

   if (typeof time === "string") {
      const isoMatch = time.match(/[T\s](\d{2}):(\d{2})/);
      if (isoMatch) return `${isoMatch[1]}:${isoMatch[2]}`;

      const timeMatch = time.match(/^(\d{1,2}):(\d{2})/);
      if (timeMatch) {
         return `${String(Number(timeMatch[1])).padStart(2, "0")}:${timeMatch[2]}`;
      }
   }

   return time ? String(time) : "";
}

function formatDate(dateValue) {
   const date = readDate({ date: dateValue });
   if (!date) return "—";

   const parsed = new Date(`${date}T00:00:00`);
   if (Number.isNaN(parsed.getTime())) return date;

   return new Intl.DateTimeFormat("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
   }).format(parsed);
}

function buildDateTimeMs(source) {
   const date = readDate(source);
   const time = readTime(source) || "00:00";
   if (!date) return Number.MAX_SAFE_INTEGER;

   const parsed = Date.parse(`${date}T${time}:00`);
   return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
}

function readGearbox(source) {
   const gearbox = readFirst(source, ["gearbox", "cutie", "transmission"], "—");
   return String(gearbox);
}

function readSector(source) {
   const sector = readFirst(source, ["sector", "location", "zone"], "—");
   return String(sector);
}

function normalizeText(value) {
   return String(value || "")
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
}

function normalizeGearboxValue(value) {
   const normalized = normalizeText(value);
   if (normalized.includes("automat") || normalized.includes("automatic")) {
      return "automat";
   }
   if (
      normalized.includes("manual") ||
      normalized.includes("mecanic") ||
      normalized.includes("mechanic")
   ) {
      return "manual";
   }
   return normalized || "manual";
}

function pad2(value) {
   return String(value).padStart(2, "0");
}

function dateKeyFromDate(date) {
   if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
   return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
      date.getDate(),
   )}`;
}

function parseDateKey(dateKey) {
   const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateKey || ""));
   if (!match) return null;

   const parsed = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
   );
   return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isPastOrToday(date) {
   const dayKey = dateKeyFromDate(date);
   const todayKey = dateKeyFromDate(new Date());
   return !dayKey || dayKey <= todayKey;
}

function readCapacity(source) {
   const capacity = Number(readFirst(source, ["capacity", "places", "seats"], 2));
   return Number.isFinite(capacity) && capacity > 0 ? Math.trunc(capacity) : 2;
}

function readUsedPlaces(source) {
   const bookedCount = readFirst(source, ["bookedCount", "booked_count"]);
   if (bookedCount != null) {
      const parsed = Number(bookedCount);
      if (Number.isFinite(parsed)) return Math.max(0, Math.trunc(parsed));
   }

   const direct = readFirst(source, [
      "booked",
      "reserved",
      "used",
      "occupied",
      "reservationsCount",
      "reservations_count",
      "bookingsCount",
      "bookings_count",
   ]);

   if (direct != null) {
      const parsed = Number(direct);
      if (Number.isFinite(parsed)) return Math.max(0, Math.trunc(parsed));
   }

   const reservations =
      source?.reservations ??
      source?.slotReservations ??
      source?.practicalExamReservations ??
      [];

   return Array.isArray(reservations) ? reservations.length : 0;
}

function readFreePlaces(source) {
   const availableSpots = readFirst(source, [
      "availableSpots",
      "available_spots",
      "freePlaces",
      "free_places",
      "remainingPlaces",
      "remaining_places",
   ]);

   if (availableSpots != null) {
      const parsed = Number(availableSpots);
      if (Number.isFinite(parsed)) return Math.max(0, Math.trunc(parsed));
   }

   return Math.max(0, readCapacity(source) - readUsedPlaces(source));
}

function isSlotFullyBooked(source) {
   return readFreePlaces(source) <= 0;
}

function isFullyBookedError(error) {
   const message = String(error?.message || "").toLowerCase();
   return (
      message.includes("409") ||
      message.includes("fully booked") ||
      message.includes("ocupat")
   );
}

function getReservationDisplaySlot(reservation) {
   return {
      ...readReservationSlot(reservation),
      date: readDate(reservation) || readDate(readReservationSlot(reservation)),
      time: readTime(reservation) || readTime(readReservationSlot(reservation)),
   };
}

function sortSlots(slots) {
   return [...slots].sort(
      (left, right) => buildDateTimeMs(left) - buildDateTimeMs(right),
   );
}

function sortReservations(reservations) {
   return [...reservations].sort((left, right) => {
      const leftSlot = getReservationDisplaySlot(left);
      const rightSlot = getReservationDisplaySlot(right);
      return buildDateTimeMs(rightSlot) - buildDateTimeMs(leftSlot);
   });
}

function readReservationIdValue(reservation) {
   return readFirst(reservation, ["id", "_id", "reservationId", "reservation_id"]);
}

function isSamePracticalReservation(left, right) {
   const leftId = readReservationIdValue(left);
   const rightId = readReservationIdValue(right);
   if (leftId != null && rightId != null) return String(leftId) === String(rightId);

   const leftSlot = getReservationDisplaySlot(left);
   const rightSlot = getReservationDisplaySlot(right);
   return (
      readDate(leftSlot) === readDate(rightSlot) &&
      readTime(leftSlot) === readTime(rightSlot) &&
      readReservationStatus(left) === readReservationStatus(right)
   );
}

function practicalCardStatusClass(status) {
   return String(normalizeStatus(status)).toLowerCase();
}

function ExamReservationCard({ reservation, current = false }) {
   const slot = getReservationDisplaySlot(reservation);
   const status = readReservationStatus(reservation);

   return (
      <article
         className={
            "practicalExamBooking__reservationCard" +
            (current ? " practicalExamBooking__reservationCard--current" : "") +
            ` practicalExamBooking__reservationCard--${practicalCardStatusClass(status)}`
         }
      >
         <p className="practicalExamBooking__reservationDate">
            {formatDate(readDate(slot))}
            {", "}
            <span>{readTime(slot) || "—"}</span>
         </p>
         <p className="practicalExamBooking__reservationMeta">
            {readSector(slot)} · {readGearbox(slot)}
         </p>
         <p className="practicalExamBooking__reservationStatus">
            {formatStatus(status)}
         </p>
      </article>
   );
}

export default function PracticalExamBooking({ variant = "page" }) {
   const { user } = useUserContext();
   const permissionKnown = hasPracticalExamFlag(user);
   const canBookFromProfile = readCanBookPracticalExam(user);
   const isPopup = variant === "popup";

   const [availableSlots, setAvailableSlots] = useState([]);
   const [activeReservation, setActiveReservation] = useState(null);
   const [history, setHistory] = useState([]);
   const [loading, setLoading] = useState(true);
   const [availableError, setAvailableError] = useState("");
   const [reservationError, setReservationError] = useState("");
   const [historyError, setHistoryError] = useState("");
   const [actionError, setActionError] = useState("");
   const [actionSlotId, setActionSlotId] = useState(null);
   const [refreshIndex, setRefreshIndex] = useState(0);
   const [stage, setStage] = useState("setup");
   const [gearboxFilter, setGearboxFilter] = useState("manual");
   const [sectorFilter, setSectorFilter] = useState(
      STUDENT_BOOKING_SECTOR_OPTIONS[0],
   );
   const [selectedDate, setSelectedDate] = useState(null);
   const [selectedSlotId, setSelectedSlotId] = useState("");
   const [availabilityChecked, setAvailabilityChecked] = useState(false);

   const { bookingWindowOpen } = usePracticalExamBookingWindow();

   const loadData = useCallback(async () => {
      setLoading(true);
      setAvailableError("");
      setReservationError("");
      setHistoryError("");
      setActionError("");

      const [availableResult, reservationResult, historyResult] =
         await Promise.allSettled([
            getAvailablePracticalExamSlots(),
            getMyPracticalExamReservation(),
            getMyPracticalExamHistory(),
         ]);

      if (availableResult.status === "fulfilled") {
         setAvailableSlots(
            Array.isArray(availableResult.value?.items)
               ? availableResult.value.items
               : [],
         );
      } else {
         setAvailableSlots([]);
         setAvailableError(
            availableResult.reason?.message ||
               "Nu am putut încărca sloturile disponibile.",
         );
      }

      if (reservationResult.status === "fulfilled") {
         setActiveReservation(unwrapOne(reservationResult.value));
      } else {
         setActiveReservation(null);
         setReservationError(
            isNoActiveReservationError(reservationResult.reason)
               ? ""
               : reservationResult.reason?.message ||
                    "Nu am putut verifica rezervarea activă.",
         );
      }

      if (historyResult.status === "fulfilled") {
         setHistory(
            Array.isArray(historyResult.value?.items)
               ? historyResult.value.items
               : [],
         );
      } else {
         setHistory([]);
         setHistoryError(
            isIgnorableHistoryError(historyResult.reason)
               ? ""
               : historyResult.reason?.message ||
                    "Nu am putut încărca istoricul examenelor.",
         );
      }

      setLoading(false);
   }, []);

   useEffect(() => {
      let cancelled = false;

      (async () => {
         await loadData();
         if (cancelled) return;
      })();

      return () => {
         cancelled = true;
      };
   }, [loadData, refreshIndex]);

   const sortedHistory = useMemo(() => sortReservations(history), [history]);
   const activeHistoryReservation = useMemo(
      () =>
         sortedHistory.find(
            (reservation) =>
               !FINAL_STATUSES.has(readReservationStatus(reservation)),
         ) || null,
      [sortedHistory],
   );
   const currentReservation = activeReservation || activeHistoryReservation;
   const sectorOptions = STUDENT_BOOKING_SECTOR_OPTIONS;

   useEffect(() => {
      if (
         !sectorOptions.some(
            (option) => normalizeText(option) === normalizeText(sectorFilter),
         )
      ) {
         setSectorFilter(sectorOptions[0]);
      }
   }, [sectorFilter, sectorOptions]);

   const criteriaMatchedSlots = useMemo(
      () =>
         sortSlots(
            availableSlots.filter((slot) => {
               const slotSector = normalizeStudentBookingSector(readSector(slot));
               const sameGearbox =
                  normalizeGearboxValue(readGearbox(slot)) === gearboxFilter;
               const sameSector = slotSector === sectorFilter;

               return slotSector && sameGearbox && sameSector;
            }),
         ),
      [availableSlots, gearboxFilter, sectorFilter],
   );

   const bookableSlots = useMemo(
      () => criteriaMatchedSlots.filter((slot) => readFreePlaces(slot) > 0),
      [criteriaMatchedSlots],
   );
   const maxSelectableDate = useMemo(() => {
      const date = new Date();
      date.setMonth(date.getMonth() + MAX_SELECTABLE_MONTHS);
      return date;
   }, []);
   const bookableDateKeys = useMemo(
      () =>
         Array.from(
            new Set(bookableSlots.map((slot) => readDate(slot)).filter(Boolean)),
         )
            .filter((dateKey) => {
               const parsed = parseDateKey(dateKey);
               return parsed && !isPastOrToday(parsed) && parsed <= maxSelectableDate;
            })
            .sort(),
      [bookableSlots, maxSelectableDate],
   );
   const availableDateKeys = useMemo(
      () =>
         Array.from(
            new Set(criteriaMatchedSlots.map((slot) => readDate(slot)).filter(Boolean)),
         )
            .filter((dateKey) => {
               const parsed = parseDateKey(dateKey);
               return parsed && !isPastOrToday(parsed) && parsed <= maxSelectableDate;
            })
            .sort(),
      [criteriaMatchedSlots, maxSelectableDate],
   );
   const availableDateSet = useMemo(
      () => new Set(availableDateKeys),
      [availableDateKeys],
   );
   const selectedDateKey = selectedDate ? dateKeyFromDate(selectedDate) : "";
   const selectedDaySlots = useMemo(
      () =>
         criteriaMatchedSlots.filter((slot) => readDate(slot) === selectedDateKey),
      [criteriaMatchedSlots, selectedDateKey],
   );
   const slotsByHourForSelectedDay = useMemo(() => {
      const map = new Map(
         PRACTICAL_EXAM_TIME_OPTIONS.map((hour) => [hour, []]),
      );

      selectedDaySlots.forEach((slot) => {
         const hour = normalizePracticalExamHour(readTime(slot));
         if (!hour || !map.has(hour)) return;
         map.get(hour).push(slot);
      });

      return map;
   }, [selectedDaySlots]);
   const selectedDayHasAnyAvailableHour = useMemo(
      () =>
         PRACTICAL_EXAM_TIME_OPTIONS.some((hour) => {
            const slots = slotsByHourForSelectedDay.get(hour) || [];
            return slots.some((slot) => readFreePlaces(slot) > 0);
         }),
      [slotsByHourForSelectedDay],
   );
   const selectedSlot = useMemo(
      () =>
         selectedDaySlots.find(
            (slot) => String(readSlotId(slot)) === String(selectedSlotId),
         ) || null,
      [selectedDaySlots, selectedSlotId],
   );
   useEffect(() => {
      setSelectedDate(null);
      setSelectedSlotId("");
      setAvailabilityChecked(false);
   }, [gearboxFilter, sectorFilter]);

   const activeStatus = readReservationStatus(currentReservation);
   const hasActiveScheduledReservation =
      currentReservation && !FINAL_STATUSES.has(activeStatus);
   const examReservationAttempts = useMemo(
      () =>
         [
            currentReservation,
            ...sortedHistory.filter(
               (reservation) =>
                  !currentReservation ||
                  !isSamePracticalReservation(currentReservation, reservation),
            ),
         ].filter(Boolean),
      [currentReservation, sortedHistory],
   );
   const passedReservation = useMemo(
      () => [currentReservation, ...sortedHistory].find(isPassedReservation) || null,
      [currentReservation, sortedHistory],
   );
   const hasPassedPracticalExam = Boolean(passedReservation);
   const bookingEligible =
      !loading &&
      !hasPassedPracticalExam &&
      !hasActiveScheduledReservation &&
      (!permissionKnown || canBookFromProfile);
   const canCreateReservation = bookingEligible && bookingWindowOpen;
   const hasCriteriaAvailability =
      !availableError && bookableSlots.length > 0;
   const canCheckAvailability = canCreateReservation;

   const handleContinue = () => {
      if (!isPracticalExamBookingWindowOpen()) {
         setActionError(PRACTICAL_EXAM_BOOKING_WINDOW_BLOCKED_MESSAGE);
         return;
      }
      if (!canCheckAvailability) return;
      setAvailabilityChecked(true);
      if (!hasCriteriaAvailability) return;

      const firstDate = bookableDateKeys.find((dateKey) => {
         const parsed = parseDateKey(dateKey);
         return parsed && !isPastOrToday(parsed);
      });

      setSelectedDate(firstDate ? parseDateKey(firstDate) : null);
      setSelectedSlotId("");
      setStage("pick");
   };

   const handleBackToSetup = () => {
      setStage("setup");
      setSelectedSlotId("");
   };

   const handleBook = async (slot) => {
      const slotId = readSlotId(slot);
      if (!slotId || !canCreateReservation) return;

      if (!isPracticalExamBookingWindowOpen()) {
         setActionError(PRACTICAL_EXAM_BOOKING_WINDOW_BLOCKED_MESSAGE);
         return;
      }

      if (isSlotFullyBooked(slot)) {
         setActionError("Această oră este deja ocupată. Alege altă oră.");
         setSelectedSlotId("");
         return;
      }

      setActionSlotId(slotId);
      setActionError("");

      try {
         const fresh = await getAvailablePracticalExamSlots();
         const freshItems = Array.isArray(fresh?.items) ? fresh.items : [];
         setAvailableSlots(freshItems);

         const freshSlot = freshItems.find(
            (item) => String(readSlotId(item)) === String(slotId),
         );

         if (!freshSlot || isSlotFullyBooked(freshSlot)) {
            setActionError(
               "Această oră a fost ocupată între timp. Selectează altă oră.",
            );
            setSelectedSlotId("");
            return;
         }

         // Rezervarea pleacă gri din start, ca programările simple.
         await bookPracticalExamSlot(slotId, {
            color: PRACTICAL_EXAM_RESERVATION_DEFAULT_COLOR,
         });
         setRefreshIndex((prev) => prev + 1);
      } catch (error) {
         if (isFullyBookedError(error)) {
            setActionError(
               "Această oră a fost deja ocupată. Selectează altă oră.",
            );
            setSelectedSlotId("");
            setRefreshIndex((prev) => prev + 1);
         } else {
            setActionError(
               error?.message ||
                  "Rezervarea nu a fost creată. Verifică accesul și locurile libere.",
            );
         }
      } finally {
         setActionSlotId(null);
      }
   };

   const handleBookSelected = () => {
      if (!selectedSlot) return;
      handleBook(selectedSlot);
   };

   return (
      <section
         className={
            "practicalExamBooking" +
            (isPopup ? " practicalExamBooking--popup" : "")
         }
      >
         <div className="studentAddReservation practicalExamBooking__flow">
            <h3 className="studentAddReservation__title-main">
               Examen practic
            </h3>

            <div className="studentAddReservation__content">
               {loading ? (
                  <div className="studentAddReservation__loading">
                     Se încarcă disponibilitatea...
                  </div>
               ) : null}

               {actionError ? (
                  <div className="studentAddReservation__box">
                     <div className="studentAddReservation__info">
                        <b>Atenție.</b>{" "}
                        {isFullyBookedError({ message: actionError })
                           ? "Această oră a fost deja ocupată. Selectează altă oră."
                           : actionError.replace(/^Eroare\.\s*/i, "")}
                     </div>
                  </div>
               ) : null}

               {reservationError ? (
                  <div className="studentAddReservation__box">
                     <div className="studentAddReservation__info">
                        <b>Atenție.</b> {reservationError}
                     </div>
                  </div>
               ) : null}

               {hasPassedPracticalExam ? (
                  <div className="studentAddReservation__box practicalExamBooking__passedNotice">
                     <div className="studentAddReservation__info">
                        <span
                           className="practicalExamBooking__passedNoticeIcon"
                           aria-hidden="true"
                        >
                           <ReactSVG
                              src={heartFullIcon}
                              className="practicalExamBooking__passedNoticeSvg"
                           />
                        </span>
                        <span className="practicalExamBooking__passedNoticeText">
                           <strong>Felicitări, ai promovat Ex. practic!</strong>
                           <span>
                              Programările noi sunt blocate, iar încercările rămân
                              afișate mai jos.
                           </span>
                        </span>
                     </div>
                  </div>
               ) : null}

               {permissionKnown && !canBookFromProfile ? (
                  <div className="studentAddReservation__box">
                     <div className="studentAddReservation__info">
                        <b>Acces blocat.</b> Un admin sau manager trebuie să activeze
                        accesul la Ex. practic.
                     </div>
                  </div>
               ) : null}

               {!loading && bookingEligible && !bookingWindowOpen ? (
                  <div className="studentAddReservation__box">
                     <div className="studentAddReservation__info">
                        <b>{PRACTICAL_EXAM_BOOKING_WINDOW_FULL_LABEL}</b>{" "}
                        {PRACTICAL_EXAM_BOOKING_WINDOW_BLOCKED_MESSAGE}
                     </div>
                  </div>
               ) : null}

               {!loading && examReservationAttempts.length > 0 ? (
                  <div className="practicalExamBooking__reservationsBlock">
                     <div className="practicalExamBooking__reservationsHead">
                        <h3>Încercări examen</h3>
                        <span>{examReservationAttempts.length}</span>
                     </div>
                     <div className="practicalExamBooking__reservationGrid">
                        {examReservationAttempts.map((reservation, index) => (
                           <ExamReservationCard
                              key={
                                 readReservationIdValue(reservation) ||
                                 `${index}-${readReservationStatus(reservation)}`
                              }
                              reservation={reservation}
                              current={
                                 hasActiveScheduledReservation &&
                                 isSamePracticalReservation(
                                    currentReservation,
                                    reservation,
                                 )
                              }
                           />
                        ))}
                     </div>
                  </div>
               ) : null}

               {!loading && canCreateReservation && stage === "setup" ? (
                  <>
                     <div className="studentAddReservation__selector studentAddReservation__selector--row">
                        <h3 className="studentAddReservation__title">
                           Selectează cutia de viteza
                        </h3>
                        <div
                           className={`studentAddReservation__radio-wrapper ${
                              gearboxFilter === "manual"
                                 ? "studentAddReservation__radio-wrapper--left"
                                 : "studentAddReservation__radio-wrapper--right"
                           }`}
                        >
                           {GEARBOX_OPTIONS.map((option) => (
                              <label key={option.value}>
                                 <input
                                    type="radio"
                                    name="practical-exam-gearbox"
                                    value={option.value}
                                    checked={gearboxFilter === option.value}
                                    onChange={(event) =>
                                       setGearboxFilter(event.target.value)
                                    }
                                 />
                                 {option.label}
                              </label>
                           ))}
                        </div>
                     </div>

                     <div className="studentAddReservation__selector studentAddReservation__selector--row">
                        <h3 className="studentAddReservation__title">
                           Selectează sectorul dorit
                        </h3>
                        <div className="practicalExamBooking__pillGrid">
                           {sectorOptions.map((sector) => (
                              <button
                                 key={sector}
                                 type="button"
                                 className={
                                    normalizeText(sector) ===
                                    normalizeText(sectorFilter)
                                       ? "is-active"
                                       : ""
                                 }
                                 onClick={() => setSectorFilter(sector)}
                              >
                                 {sector}
                              </button>
                           ))}
                        </div>
                     </div>

                     <div className="studentAddReservation__add-btns">
                        <button
                           onClick={handleContinue}
                           disabled={!canCheckAvailability}
                           className="studentAddReservation__add-btn studentAddReservation__add-btn--arrow studentAddReservation__add-btn--full"
                           type="button"
                        >
                           <span>Arată disponibilitatea</span>
                        </button>
                     </div>

                     {availabilityChecked && availableError ? (
                        <div className="studentAddReservation__box">
                           <div className="studentAddReservation__info">
                              <b>Atenție.</b> {availableError}
                           </div>
                        </div>
                     ) : null}

                     {availabilityChecked &&
                     !availableError &&
                     bookableDateKeys.length === 0 ? (
                        <div className="studentAddReservation__box">
                           <div className="studentAddReservation__info">
                              Nu există sloturi libere pentru criteriile selectate.
                           </div>
                        </div>
                     ) : null}
                  </>
               ) : null}

               {!loading && canCreateReservation && stage === "pick" ? (
                  <>
                     <div className="studentAddReservation__pick-topbar">
                        <button
                           onClick={handleBackToSetup}
                           className="studentAddReservation__add-btn studentAddReservation__add-btn--back"
                           type="button"
                           title="Înapoi la criterii"
                        >
                           <ReactSVG
                              src={arrowIcon}
                              className="studentAddReservation__add-btn-icon"
                           />
                           <span>Înapoi la criterii</span>
                        </button>

                        <button
                           type="button"
                           className="studentAddReservation__add-btn studentAddReservation__add-btn--list"
                           onClick={() => setRefreshIndex((prev) => prev + 1)}
                           disabled={loading}
                           title="Reîncarcă disponibilitatea"
                        >
                           <span>Reîncarcă</span>
                        </button>
                     </div>

                     <div className="studentAddReservation__selector">
                        <div className="studentAddReservation__calendar">
                           <h3 className="studentAddReservation__title">
                              Selectează data și ora:
                           </h3>

                           <DatePicker
                              selected={selectedDate}
                              onChange={(date) => {
                                 setSelectedDate(date);
                                 setSelectedSlotId("");
                              }}
                              inline
                              locale="ro"
                              formatWeekDay={(name) =>
                                 name
                                    .substring(0, 2)
                                    .replace(/^./, (char) => char.toUpperCase())
                              }
                              minDate={new Date()}
                              maxDate={maxSelectableDate}
                              filterDate={(date) =>
                                 !isPastOrToday(date) &&
                                 availableDateSet.has(dateKeyFromDate(date))
                              }
                              dayClassName={(date) =>
                                 isPastOrToday(date) ||
                                 !availableDateSet.has(dateKeyFromDate(date))
                                    ? "studentAddReservation__day--inactive"
                                    : ""
                              }
                           />
                        </div>

                        <div className="studentAddReservation__times">
                           <h3 className="studentAddReservation__title studentAddReservation__times-title">
                              Selectează:
                           </h3>

                           <div
                              className="studentAddReservation__times-list practicalExamBooking__timesList"
                              style={{
                                 gridTemplateColumns: `repeat(${PRACTICAL_EXAM_TIME_GRID_COLUMNS}, minmax(0, 1fr))`,
                              }}
                           >
                              {availableError ? (
                                 <div className="studentAddReservation__disclaimer">
                                    {availableError}
                                 </div>
                              ) : null}

                              {!availableError && availableDateKeys.length === 0 ? (
                                 <div className="studentAddReservation__disclaimer">
                                    Nu există sloturi pentru criteriile selectate.
                                 </div>
                              ) : null}

                              {!availableError &&
                              availableDateKeys.length > 0 &&
                              !selectedDate ? (
                                 <div className="studentAddReservation__disclaimer">
                                    Te rog să selectezi mai întâi o zi.
                                 </div>
                              ) : null}

                              {!availableError &&
                              availableDateKeys.length > 0 &&
                              selectedDate &&
                              selectedDaySlots.length === 0 ? (
                                 <div className="studentAddReservation__disclaimer">
                                    Nu există ore pentru această zi.
                                 </div>
                              ) : null}

                              {!availableError &&
                              availableDateKeys.length > 0 &&
                              selectedDate &&
                              selectedDaySlots.length > 0 &&
                              !selectedDayHasAnyAvailableHour ? (
                                 <div className="studentAddReservation__disclaimer">
                                    Toate orele din această zi sunt deja ocupate.
                                 </div>
                              ) : null}

                              {PRACTICAL_EXAM_TIME_OPTIONS.map((hour) => {
                                 const slotsForHour =
                                    slotsByHourForSelectedDay.get(hour) || [];
                                 const availableSlot =
                                    slotsForHour.find(
                                       (slot) => readFreePlaces(slot) > 0,
                                    ) || null;
                                 const displaySlot =
                                    availableSlot || slotsForHour[0] || null;
                                 const slotId = displaySlot
                                    ? readSlotId(displaySlot)
                                    : "";
                                 const freePlaces = slotsForHour.reduce(
                                    (sum, slot) => sum + readFreePlaces(slot),
                                    0,
                                 );
                                 const capacity = displaySlot
                                    ? readCapacity(displaySlot)
                                    : 0;
                                 const usedPlaces = displaySlot
                                    ? readUsedPlaces(displaySlot)
                                    : 0;
                                 const hasSlot = slotsForHour.length > 0;
                                 const hasAvailability = Boolean(availableSlot);
                                 const isSelected =
                                    hasAvailability &&
                                    String(selectedSlotId) ===
                                       String(readSlotId(availableSlot));
                                 const isFullyOccupied =
                                    hasSlot && !hasAvailability;
                                 const disabled =
                                    !hasAvailability ||
                                    (slotId &&
                                       actionSlotId === String(slotId));

                                 return (
                                    <button
                                       key={hour}
                                       onClick={() => {
                                          if (!availableSlot) return;
                                          setSelectedSlotId(
                                             String(readSlotId(availableSlot)),
                                          );
                                       }}
                                       disabled={disabled}
                                       className={`studentAddReservation__time-btn practicalExamBooking__timeBtn ${
                                          isSelected
                                             ? "studentAddReservation__time-btn--selected"
                                             : ""
                                       } ${
                                          isFullyOccupied
                                             ? "practicalExamBooking__timeBtn--occupied studentAddReservation__time-btn--disabled"
                                             : !hasAvailability
                                               ? "studentAddReservation__time-btn--disabled"
                                               : ""
                                       }`}
                                       title={
                                          !hasSlot
                                             ? "Nicio programare la această oră"
                                             : isFullyOccupied
                                               ? `Ocupat (${usedPlaces}/${capacity} locuri)`
                                               : `Disponibil (${freePlaces} locuri libere)`
                                       }
                                       type="button"
                                    >
                                       <span>{hour}</span>
                                    </button>
                                 );
                              })}
                           </div>
                        </div>
                     </div>

                     <div className="studentAddReservation__add-btns studentAddReservation__add-btns--single">
                        <button
                           onClick={handleBookSelected}
                           disabled={
                              !selectedSlot ||
                              !canCreateReservation ||
                              isSlotFullyBooked(selectedSlot) ||
                              actionSlotId === readSlotId(selectedSlot)
                           }
                           className="studentAddReservation__add-btn studentAddReservation__add-btn--arrow studentAddReservation__add-btn--full"
                           type="button"
                        >
                           <span>
                              {selectedSlot &&
                              actionSlotId === readSlotId(selectedSlot)
                                 ? "Se rezervă..."
                                 : "Rezervă Ex. practic"}
                           </span>
                        </button>
                     </div>
                  </>
               ) : null}

               {!loading && historyError ? (
                  <div className="studentAddReservation__box">
                     <div className="studentAddReservation__info">
                        <b>Atenție.</b> {historyError}
                     </div>
                  </div>
               ) : null}

            </div>
         </div>
      </section>
   );
}
